import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CarouselItem {
  id: string;
  type: 'listing' | 'nutritionist' | 'physiotherapist';
  [key: string]: any;
}

export const useFeaturedCarousel = (filterType?: 'nutritionist' | 'physiotherapist', limit: number = 10) => {
  const [featuredCarousel, setFeaturedCarousel] = useState<CarouselItem[]>([]);
  const [allProfessionals, setAllProfessionals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedCarousel = async () => {
      setLoading(true);
      try {
        const items: CarouselItem[] = [];

        // If filterType is specified, only fetch that type
        if (filterType === 'nutritionist') {
          // Fetch from nutritionist_profiles
          const { data: profileData } = await supabase
            .from("nutritionist_profiles")
            .select("*")
            .eq("is_active", true)
            .limit(100);

          // Fetch from listings with type='nutrition'
          const { data: listingsData } = await supabase
            .from("listings")
            .select("*")
            .eq("status", "active")
            .eq("type", "nutrition")
            .limit(100);

          const combined = [
            ...(profileData || []).map(p => ({ ...p, type: 'nutritionist' as const, source: 'profile' })),
            ...(listingsData || []).map(l => ({ 
              ...l, 
              type: 'nutritionist' as const, 
              source: 'listing',
              full_name: l.name,
              profile_image: l.images?.[0] || null,
              location_city: l.city,
              specialty: l.short_description || 'Nutrición',
              years_experience: null,
              location_country: l.country
            }))
          ];

          if (combined.length > 0) {
            setAllProfessionals(combined);
            const shuffled = combined.sort(() => Math.random() - 0.5);
            items.push(...shuffled.slice(0, limit));
          }
        } else if (filterType === 'physiotherapist') {
          // Fetch from physiotherapist_profiles
          const { data: profileData } = await supabase
            .from("physiotherapist_profiles")
            .select("*")
            .eq("is_active", true)
            .limit(100);

          // Fetch from listings with type='physiotherapy'
          const { data: listingsData } = await supabase
            .from("listings")
            .select("*")
            .eq("status", "active")
            .eq("type", "physiotherapy")
            .limit(100);

          const combined = [
            ...(profileData || []).map(p => ({ ...p, type: 'physiotherapist' as const, source: 'profile' })),
            ...(listingsData || []).map(l => ({ 
              ...l, 
              type: 'physiotherapist' as const,
              source: 'listing',
              full_name: l.name,
              profile_image: l.images?.[0] || null,
              location_city: l.city,
              specialty: l.short_description || 'Fisioterapia',
              years_experience: null,
              location_country: l.country
            }))
          ];

          if (combined.length > 0) {
            setAllProfessionals(combined);
            const shuffled = combined.sort(() => Math.random() - 0.5);
            items.push(...shuffled.slice(0, limit));
          }
        } else {
          // Mixed carousel: premium gyms, nutritionists, physiotherapists
          const { data: premiumListings } = await supabase
            .from("listings")
            .select("*")
            .eq("status", "active")
            .eq("is_premium", true)
            .limit(6);

          if (premiumListings) {
            items.push(...premiumListings.map(item => ({ ...item, type: 'listing' as const })));
          }

          // Fetch nutritionists from profiles
          const { data: nutritionistProfiles } = await supabase
            .from("nutritionist_profiles")
            .select("*")
            .eq("is_active", true)
            .limit(20);

          // Fetch nutritionists from listings
          const { data: nutritionistListings } = await supabase
            .from("listings")
            .select("*")
            .eq("status", "active")
            .eq("type", "nutrition")
            .limit(20);

          // Fetch physiotherapists from profiles
          const { data: physiotherapistProfiles } = await supabase
            .from("physiotherapist_profiles")
            .select("*")
            .eq("is_active", true)
            .limit(20);

          // Fetch physiotherapists from listings
          const { data: physiotherapistListings } = await supabase
            .from("listings")
            .select("*")
            .eq("status", "active")
            .eq("type", "physiotherapy")
            .limit(20);

          const remainingSlots = 10 - items.length;
          const professionals: any[] = [];

          // Add nutritionist profiles
          if (nutritionistProfiles) {
            professionals.push(...nutritionistProfiles.map(n => ({ 
              ...n, 
              type: 'nutritionist' as const,
              source: 'profile'
            })));
          }

          // Add nutritionist listings
          if (nutritionistListings) {
            professionals.push(...nutritionistListings.map(l => ({ 
              ...l, 
              type: 'nutritionist' as const,
              source: 'listing',
              full_name: l.name,
              profile_image: l.images?.[0] || null,
              location_city: l.city,
              specialty: l.short_description || 'Nutrición',
              years_experience: null,
              location_country: l.country
            })));
          }

          // Add physiotherapist profiles
          if (physiotherapistProfiles) {
            professionals.push(...physiotherapistProfiles.map(p => ({ 
              ...p, 
              type: 'physiotherapist' as const,
              source: 'profile'
            })));
          }

          // Add physiotherapist listings
          if (physiotherapistListings) {
            professionals.push(...physiotherapistListings.map(l => ({ 
              ...l, 
              type: 'physiotherapist' as const,
              source: 'listing',
              full_name: l.name,
              profile_image: l.images?.[0] || null,
              location_city: l.city,
              specialty: l.short_description || 'Fisioterapia',
              years_experience: null,
              location_country: l.country
            })));
          }

          // Shuffle and take remaining slots
          const shuffledProfessionals = professionals.sort(() => Math.random() - 0.5);
          items.push(...shuffledProfessionals.slice(0, remainingSlots));
        }

        // Shuffle and limit
        const shuffled = items.sort(() => Math.random() - 0.5).slice(0, limit);
        setFeaturedCarousel(shuffled);
      } catch (error) {
        console.error("Error fetching featured carousel:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedCarousel();
  }, [filterType, limit]);

  return { featuredCarousel, allProfessionals, loading };
};
